import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// Layout
import Layout from './components/Layout';

// Scroll to top on navigation
import ScrollToTop from './components/ScrollToTop';

// Analytics
import { Analytics } from './components/Analytics';

// Page Editor
import { PageEditorProvider } from './components/PageEditorContext';
import EditModeToolbar from './components/EditModeToolbar';

// Home stays eager for instant first paint
import Home from './pages/Home';

// All other pages are code-split
const Listen = lazy(() => import('./pages/Listen'));
const EpisodePage = lazy(() => import('./pages/EpisodePage'));
const Watch = lazy(() => import('./pages/Watch'));
const Shorts = lazy(() => import('./pages/Shorts'));
const Learn = lazy(() => import('./pages/Learn'));
const Topics = lazy(() => import('./pages/Topics'));
const TopicPage = lazy(() => import('./pages/TopicPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const CaseStudyPage = lazy(() => import('./pages/CaseStudyPage'));
const BookACall = lazy(() => import('./pages/BookACall'));
const StartHere = lazy(() => import('./pages/StartHere'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const ForClinicOwners = lazy(() => import('./pages/ForClinicOwners'));
const ProfessionLanding = lazy(() => import('./pages/ProfessionLanding'));
const CityLanding = lazy(() => import('./pages/CityLanding'));
const PodcastForClinicOwnersUK = lazy(() => import('./pages/PodcastForClinicOwnersUK'));
const ClinicMarketingResources = lazy(() => import('./pages/ClinicMarketingResources'));
const AestheticsBusinessPodcast = lazy(() => import('./pages/AestheticsBusinessPodcast'));
const HowToGrowClinicRevenue = lazy(() => import('./pages/HowToGrowClinicRevenue'));
const ClinicOwnerCommunity = lazy(() => import('./pages/ClinicOwnerCommunity'));
const AboutKatieBell = lazy(() => import('./pages/AboutKatieBell'));
const WorkWithKatie = lazy(() => import('./pages/WorkWithKatie'));
const FAQLibrary = lazy(() => import('./pages/FAQLibrary'));
const ElevateProgram = lazy(() => import('./pages/ElevateProgram'));
const AscendProgram = lazy(() => import('./pages/AscendProgram'));
const PartnersSponsors = lazy(() => import('./pages/PartnersSponsors'));
const Admin = lazy(() => import('./pages/Admin'));
const KatiesPlan = lazy(() => import('./pages/KatiesPlan'));

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-[#E91E8C] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// SEO-preserving redirects for old WordPress site URLs (tags, feeds, old posts, etc.)
const LEGACY_EXACT = {
  '/terms-conditions': '/terms',
  '/testimonial-disclaimer': '/terms',
  '/leadership': '/topics/leadership',
  '/the-why': '/about-katie-bell',
  '/success-stories': '/',
  '/mindset': '/topics/mindset',
  '/marketing': '/topics/marketing'
};

const LegacyRedirect = () => {
  const { pathname } = useLocation();
  const p = pathname.toLowerCase().replace(/\/feed\/?$/, '/').replace(/\/+$/, '');
  let target = '/';
  if (LEGACY_EXACT[p]) {
    target = LEGACY_EXACT[p];
  } else if (p.startsWith('/tag/') || p.startsWith('/category/') || p.startsWith('/author/') || p === '/tag' || p === '/category') {
    target = '/topics';
  } else if (p.startsWith('/podcast') || p.startsWith('/episode')) {
    target = '/listen';
  } else if (p.startsWith('/blog') || p.startsWith('/news') || p.startsWith('/articles')) {
    target = '/learn';
  } else if (p.startsWith('/about') || p.startsWith('/meet') || p.startsWith('/team')) {
    target = '/about-katie-bell';
  } else if (p.startsWith('/contact') || p.startsWith('/book')) {
    target = '/book-a-call';
  } else if (p.startsWith('/coaching') || p.startsWith('/programme') || p.startsWith('/program') || p.startsWith('/activate') || p.startsWith('/masters') || p.startsWith('/momentum')) {
    target = '/work-with-katie';
  } else if ((p.match(/-/g) || []).length >= 2) {
    target = '/learn';
  }
  return <Navigate to={target} replace />;
};

function App() {
  useEffect(() => {
    const ref = document.referrer;
    if (ref && !ref.includes(window.location.hostname) && !sessionStorage.getItem('ref_tracked')) {
      sessionStorage.setItem('ref_tracked', '1');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/referral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referrer: ref, path: window.location.pathname })
      }).catch(() => {});
    }
  }, []);
  return (
    <BrowserRouter>
      <PageEditorProvider>
        <ScrollToTop />
        <Analytics />
        <EditModeToolbar />
        <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Katie's Plan - standalone pages (own header/footer, no site layout) */}
          <Route path="katies-plan" element={<KatiesPlan variant="main" />} />
          <Route path="katies-plan/thanks" element={<KatiesPlan variant="thanks" />} />
          <Route path="katies-plan/podcast" element={<KatiesPlan variant="podcast" />} />
          <Route path="katies-plan/booked" element={<KatiesPlan variant="booked" />} />
          <Route path="katies-plan/uk-ireland" element={<KatiesPlan variant="location" />} />
          <Route path="katies-plan/business-type" element={<KatiesPlan variant="business-type" />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="listen" element={<Listen />} />
            <Route path="podcast/:slug" element={<EpisodePage />} />
            <Route path="watch" element={<Watch />} />
            <Route path="watch/:slug" element={<Watch />} />
            <Route path="shorts" element={<Shorts />} />
            <Route path="learn" element={<Learn />} />
            <Route path="learn/:slug" element={<BlogPage />} />
            <Route path="client-success" element={<CaseStudies />} />
            <Route path="client-success/:slug" element={<CaseStudyPage />} />
            <Route path="topics" element={<Topics />} />
            <Route path="topics/:topicId" element={<TopicPage />} />
            <Route path="book-a-call" element={<Navigate to="/katies-plan" replace />} />
            {/* Redirect start-here to StartHere page */}
            <Route path="start-here" element={<StartHere />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<Terms />} />
            <Route path="cookie-policy" element={<CookiePolicy />} />
            
            {/* Evergreen SEO Landing Pages */}
            <Route path="for/clinic-owners" element={<ForClinicOwners />} />
            <Route path="physiotherapy-clinic-business-coaching" element={<ProfessionLanding profession="physiotherapy" />} />
            <Route path="osteopathy-clinic-business-coaching" element={<ProfessionLanding profession="osteopathy" />} />
            <Route path="sports-therapy-clinic-business-coaching" element={<ProfessionLanding profession="sports-therapy" />} />
            <Route path="podiatry-clinic-business-coaching" element={<ProfessionLanding profession="podiatry" />} />
            <Route path="clinic-business-coaching/:city" element={<CityLanding />} />
            <Route path="podcast-for-clinic-owners-uk" element={<PodcastForClinicOwnersUK />} />
            <Route path="clinic-marketing-resources" element={<ClinicMarketingResources />} />
            <Route path="aesthetics-business-podcast" element={<AestheticsBusinessPodcast />} />
            <Route path="how-to-grow-clinic-revenue" element={<HowToGrowClinicRevenue />} />
            <Route path="clinic-owner-community" element={<ClinicOwnerCommunity />} />
            <Route path="about-katie-bell" element={<AboutKatieBell />} />
            <Route path="work-with-katie" element={<WorkWithKatie />} />
            <Route path="foundations" element={<Navigate to="/elevate" replace />} />
            <Route path="elevate" element={<ElevateProgram />} />
            <Route path="ascend" element={<AscendProgram />} />
            <Route path="partners-sponsors" element={<PartnersSponsors />} />
            <Route path="faq-library" element={<FAQLibrary />} />
            <Route path="questions" element={<FAQLibrary />} />
            
            {/* Admin Dashboard */}
            <Route path="admin" element={<Admin />} />
            
            {/* Legacy URL catch-all (preserves SEO from old WordPress site) */}
            <Route path="*" element={<LegacyRedirect />} />
          </Route>
        </Routes>
        </Suspense>
      </PageEditorProvider>
    </BrowserRouter>
  );
}

export default App;
